import {
  Dictionary,
  filterApplianceInformation,
  filterInformation,
  filterSubsystems,
  FloorPlanAnchorType,
  FloorPlanInspectionType,
  FloorPlanLevelType,
  FORMAT_PPP,
  hideStakeholder,
  hideSubsystem,
  HOME_INSPECTION_DETAILS_NAME,
  includesDrawInspection,
  includesSowInspection,
  includeSystemSubSystems,
  InspectionType,
  OrderType,
  ReInspectionStatus,
  ReportApplianceInformationType,
  ReportAttachmentType,
  ReportDeficiencyType,
  ReportDeliveryFilter,
  ReportFilterEnum,
  ReportItemEnum,
  ReportMedia,
  ReportSeverity,
  ReportStatusEnum,
  ReportSubsystemType,
  ReportSubSystemTypeWithGroup,
  ReportSystemGroupType,
  ReportSystemType,
  ReportSystemTypeWithGroup,
  ReportType,
  sortByDisplayIndex,
  sortByDisplayIndexAndHomeDetails,
  toCapitalizedSentence,
} from '@inspectify/core';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  GroupedDeficienciesBySeverity,
  IssueType,
  LimitationSummaryRowType,
  ReportAttachmentListType,
  SectionType,
  SowSummaryRowType,
} from '../../../types';
import { haversineDistance, idToHtmlId } from '../../../utils';
import { metersToFeet } from '../../../utils/geo/conversions';
import { RootState } from '../../store';
import { computeWarrantyStatus, WarrantyStateType } from './warranty';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { Maybe, PropertyType } from "../../../gql/generated/queries";
import { LocalStorageKey } from "../../../vendor/local";
import { getIssueIcon } from "../../../utils/report";
import { format, fromUnixTime } from "date-fns";

export interface ReportState {
  selectedReportId: string | null;
  selectedInspectionId: string | null;
  report: ReportType;
  order: OrderType;
  groupedReportSubsystemsBySystemName: ReportSystemTypeWithGroup[];
  warranty?: WarrantyStateType;
  // FloorPlan
  floorPlanInspections: FloorPlanInspectionType[];
  floorPlanLevels: Record<string, FloorPlanLevelType>; // Record of floor name to floor plan
  // flags
  isDenverPdf?: boolean;
  isReInspection?: boolean;
  isSOW: boolean;
  isDraw: boolean;
  //
  options: {
    hideDeficiencies: boolean;
    filters: Record<string, any>,
    groups?: Dictionary<ReportSystemGroupType>,
  };
}

// @ts-ignore
export const initialState: ReportState = {
  // report id
  selectedReportId: null,
  selectedInspectionId: null,
  // original report
  report: {
    __typename: 'ReportType',
    id: '',
    complianceText: '',
    createdAt: undefined,
    geotaggingRequired: false,
    enableManualPinning: false,
    videoCaptureEnabled: false,
    includeDrawDisbursementFields: false,
    includePermitData: false,
    includeWideAnglePhotos: false,
    inspectionDocuments: [],
    inspectionReports: [],
    noDeficienciesAllowed: false,
    noLimitationsAllowed: false,
    orderId: '',
    originalReport: undefined,
    originalReportId: '',
    pdfDocumentUrl: '',
    publishedAt: undefined,
    reportApplianceInformations: [],
    reportAttachments: [],
    reportLocations: [],
    reportSystemGroups: [],
    reportSystems: [],
    startedAt: undefined,
    templateNames: [],
    templateReportIds: [],
    updatedAt: undefined,
    autoDeficiencyLocationRequired: false,
  },
  order: {
    accessInformations: [],
    id: '',
    inspections: [],
    isAppointmentBased: false,
    isMock: false,
    orderStakeholders: [],
    orderUnits: [],
    propertyId: '',
    status: '',
    teamId: '',
  },
  // report compiled data
  groupedReportSubsystemsBySystemName: [],
  warranty: undefined,
  // FloorPlan
  floorPlanInspections: [],
  floorPlanLevels: {},
  // flags
  isDenverPdf: false,
  isReInspection: false,
  isSOW: false,
  isDraw: false,
  // report options
  options: {
    hideDeficiencies: false,
    filters: {
      items: ReportFilterEnum.FullReport,
      groups: {},
    },
  },
};

export const reportSlice = createSlice({
  name: 'report',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setCurrentReportId: (state, action) => ({ ...state, selectedReportId: action.payload }),
    setReport: (state, action: PayloadAction<ReportType | undefined>) => {
      if (!action.payload) return state;

      const report = action.payload as ReportType;

      const selectedInspectionId = state.order.inspections?.find(inspection =>
        inspection.inspectionReports?.some(ir => ir?.report?.id === report.id)
      )?.id || null;

      // filter out systems that are not included on the report
      const filteredSystems: ReportSystemType[] = report?.reportSystems
        ?.filter(includeSystemSubSystems);

      const groupedReportSubsystemsBySystemName = filteredSystems.reduce((acc, system) => {
        const subsystems = system.reportSubsystems.map(subsystem => ({
          ...subsystem,
          // add the group information to the subsystem
          group: report?.reportSystemGroups?.find(g => g.id === system.reportSystemGroupId),
        }));
        const systemByName = acc.find(s => s.name === system.name);
        if (systemByName) {
          systemByName.reportSubsystems.push(...subsystems);
        } else {
          acc.push({ ...system, reportSubsystems: subsystems });
        }
        return acc;
      }, [] as ReportSystemTypeWithGroup[])
        // sort systems by display index
        ?.sort(sortByDisplayIndexAndHomeDetails)
        // sort subsystems by display index
        ?.map(({ reportSubsystems, ...rest }) => ({
          ...rest,
          reportSubsystems: reportSubsystems.filter(hideSubsystem).sort(sortByDisplayIndex)
        }) as ReportSystemTypeWithGroup);

      const isDenverPdf = report?.inspectionReports?.some(({ inspection }) => inspection?.inspectionType === 'denver_rental_compliance');

      const isReInspection = report?.inspectionReports?.some(({ inspection }) => inspection?.inspectionType === 're_inspection');

      return {
        ...state,
        selectedReportId: report.id,
        selectedInspectionId,
        report: { ...report },
        groupedReportSubsystemsBySystemName,
        isDenverPdf,
        isReInspection,
        isSOW: includesSowInspection(filteredSystems),
        isDraw: includesDrawInspection(filteredSystems),
        options: { ...state.options },
      };
    },
    setOrder: (state, action: PayloadAction<OrderType | undefined>) => {
      if (!action.payload) return state;
      const warranty = computeWarrantyStatus(action.payload.inspections, action.payload?.id);

      return ({
        ...state,
        order: {
          ...state.order,
          ...action.payload,
        },
        warranty
      });
    },
    setOptions: (state, action) => ({ ...state, options: { ...state.options, ...action.payload } }),
    setFilters: (state, action) => ({
      ...state,
      options: {
        ...state.options,
        filters: { ...state.options.filters, ...action.payload }
      }
    }),
    addReportAttachment: (state, action) => {
      const {
        itemId,
        itemType,
        reportAttachment,
      } = action.payload;
      // https://redux-toolkit.js.org/usage/immer-reducers
      switch (itemType as ReportItemEnum) {
        case ReportItemEnum.System:
          state.report.reportSystems.find(s => s.id === itemId)?.reportAttachments.push(reportAttachment);
          break;
        case ReportItemEnum.Subsystem:
          state.report.reportSystems.flatMap(s => s.reportSubsystems)
            .find(ss => ss.id === itemId)?.reportAttachments
            .push(reportAttachment);
          break;
        case ReportItemEnum.Deficiency:
          state.report.reportSystems.flatMap(s => s.reportSubsystems)
            .flatMap(ss => ss.reportDeficiencies)
            .find(d => d.id === itemId)?.reportAttachments
            .push(reportAttachment);
          break;
        case ReportItemEnum.Information:
          state.report.reportSystems.flatMap(s => s.reportSubsystems)
            .flatMap(ss => ss.reportInformations)
            .find(i => i.id === itemId)?.reportAttachments
            .push(reportAttachment);
          break;
        case ReportItemEnum.Limitation:
          state.report.reportSystems.flatMap(s => s.reportSubsystems)
            .flatMap(ss => ss.reportLimitations)
            .find(l => l.id === itemId)?.reportAttachments
            .push(reportAttachment);
          break;
        case ReportItemEnum.ApplianceInformation:
          state.report.reportApplianceInformations
            .find(ai => ai.id === itemId)?.reportAttachments
            .push(reportAttachment);
          break;
      }
    },
    setFloorPlans: (state, action: PayloadAction<OrderType>) => {
      const reportId = state.selectedReportId;
      const groupedByReportId = action.payload?.inspections
        .reduce((acc, inspection) => {
          inspection.inspectionReports.forEach(ir => {
            if (ir?.report?.id === reportId) {
              acc[ir.report.id] = [...acc[ir.report.id] || [], inspection];
            }
          });
          return acc;
        }, {} as Record<string, InspectionType[]>);
      const floorPlanInspections = groupedByReportId?.[reportId!]?.flatMap(r => r.floorPlanInspections);

      // list of floor plans with floor name as key
      const floorPlanInspection = floorPlanInspections?.[0];
      const floorPlanLevels = floorPlanInspection?.floorPlanLevels?.reduce((acc, floorPlanLevel) => {
        const floorName = floorPlanLevel.floorName || 'Unknown Floor';
        if (!acc[floorName]) {
          acc[floorName] = {} as FloorPlanLevelType;
        }
        acc[floorName] = floorPlanLevel as FloorPlanLevelType;
        return acc;
      }, {} as Record<string, FloorPlanLevelType>) || {};

      return {
        ...state,
        floorPlanLevels,
        floorPlanInspections,
      };
    },
    updateFloorPlanLevel: (state, action: PayloadAction<FloorPlanLevelType>) => {
      const { floorName, ...floorPlanLevel } = action.payload;
      if (!state.floorPlanLevels) return state;

      // update the floor plan level in the floor plans
      state.floorPlanLevels[floorName] = {
        ...state.floorPlanLevels[floorName],
        ...floorPlanLevel,
      };

      return state;
    },
    setHideDeficiencies: (state, action: PayloadAction<boolean>) => {
      const hideDeficiencies = action.payload;
      return {
        ...state,
        report: {
          ...state.report,
          hideDeficiencies,
        },
      };
    }
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  // extraReducers: (builder) => {  },
});

export const reportActions = reportSlice.actions;

export default reportSlice.reducer;

// selectors

export const selectReport = (state: RootState) => state.selectedReport;
export const selectReportId = (state: RootState) => state.selectedReport.report.id;
export const selectReportOrderId = (state: RootState) => state.selectedReport?.report?.orderId;
export const selectCurrentInspectionId = (state: RootState) => state.selectedReport.selectedInspectionId;
export const selectOrder = (state: RootState) => state.selectedReport.order;
export const selectTeam = (state: RootState) => state.selectedReport.order.team;
export const selectProperty = (state: RootState) => state.selectedReport.order.property;
export const selectCurrentReportId = (state: RootState) =>
  state.selectedReport.selectedReportId
  || state.selectedReport.report?.id
  ;
export const selectCurrentReport = (state: RootState) => state.selectedReport.report as ReportType;
export const selectReportOptions = (state: RootState) => state.selectedReport.options;
export const selectReportOptionsFilters = (state: RootState) => state.selectedReport.options.filters;
export const selectReportWarranty = (state: RootState) => state.selectedReport.warranty;
export const selectIsDenverPdf = (state: RootState) => state.selectedReport.isDenverPdf;
export const selectIsReInspection = (state: RootState) => state.selectedReport.isReInspection;
export const selectFloorPlanLevels = (state: RootState) => state.selectedReport.floorPlanLevels;
export const selectFloorPlanInspections = (state: RootState) => state.selectedReport.floorPlanInspections;

export const selectFloorPlanAnchors = createSelector(
  [selectFloorPlanLevels],
  (floorPlanLevels) => Object.values(floorPlanLevels).flatMap((level) => level.floorPlanAnchors)
)

export const selectUnitNames =
  createSelector([selectCurrentReport], (report) => report.reportSystemGroups.map(group => group.name as string));

export const selectReportPageTitle = createSelector(
  [selectReport],
  (selectedReport,): string => {
    const { order, report } = selectedReport;
    const types = order?.inspections?.reduce((acc: Array<string>, inspection) => {
      if (inspection?.orderId === report.orderId && acc.indexOf(inspection?.inspectionType) === -1) {
        acc.push(inspection?.inspectionType);
      }
      return acc;
    }, []);
    const sentence = toCapitalizedSentence(types) || 'Inspection';
    return `${order?.property?.address || 'Report'} - ${sentence}`;
  },
);

/**
 * Build a list of sections for the report drawer
 * Grouped by system, subsystem, and reportSystemGroup
 */
export type ExtendedTreeViewBaseItem = TreeViewBaseItem<SectionType> & {
  disabled?: boolean;
};
export const selectReportDrawerSections = createSelector(
  [selectReport],
  (selectedReport: ReportState): ExtendedTreeViewBaseItem[] => {
    const filter: ReportFilterEnum = selectedReport.options.filters.items;
    const isSOW = selectedReport.isSOW;

    // merge subsystems with the same name, to show ony one of them, but also calculate the total number of issues
    const groupedReportSubsystemsBySystemName = selectedReport.groupedReportSubsystemsBySystemName
      .map(system => {
        const reduce = system.reportSubsystems.reduce((acc, subsystem) => {
          const subsystemByName = acc.find(s => s.name === subsystem.name);
          if (subsystemByName) {
            subsystemByName.reportDeficiencies = [...subsystemByName.reportDeficiencies, ...subsystem.reportDeficiencies];
            subsystemByName.reportLimitations = [...subsystemByName.reportLimitations, ...subsystem.reportLimitations];
            subsystemByName.reportInformations = [...subsystemByName.reportInformations, ...subsystem.reportInformations];
          } else {
            acc.push({ ...subsystem });
          }
          return acc;
        }, [] as ReportSubsystemType[]);
        return { ...system, reportSubsystems: reduce };
      });

    const sections = groupedReportSubsystemsBySystemName.map(system => {
      const limitationCount = system.reportSubsystems.reduce(
        (acc, subsystem) => acc + (subsystem.reportLimitations?.length || 0),
        0
      );

      const deficienciesCount = system.reportSubsystems.reduce(
        (acc, subsystem) => acc + (subsystem.reportDeficiencies?.length || 0),
        0
      );

      const notHomeDetails = system.name !== HOME_INSPECTION_DETAILS_NAME;
      let systemIssueCount = limitationCount + deficienciesCount;

      if (isSOW) {
        const incompleteSubsystems = system.reportSubsystems.filter(subsystem =>
          subsystem.reportInformations
            ?.filter(({ dataName }) => dataName?.includes('sow_completed'))
            .some(info => {
              const normalizedValue =
                typeof info.value === 'string' ? info.value.replace(/^"(.*)"$/, '$1') : info.value;
              return normalizedValue !== 'Complete';
            })
        );
        systemIssueCount += incompleteSubsystems.length;
      }

      const isParentDisabled = !systemIssueCount && filter === ReportFilterEnum.IssuesOnly;

      return {
        id: system.id,
        label: system.name || '',
        path: idToHtmlId('#system', system.id),
        extra: systemIssueCount || null,
        disabled: isParentDisabled,
        children: notHomeDetails
          ? system.reportSubsystems.map(subsystem => {
            const childrenDeficiencies = subsystem.reportDeficiencies?.map(deficiency => ({
              id: deficiency.id,
              label: deficiency.title || '',
              icon: getIssueIcon(getIssueTypeForDefect(deficiency), true),
              path: idToHtmlId('#deficiency', deficiency.id),
            }));

            const childrenLimitations = subsystem.reportLimitations?.map(limitation => ({
              id: limitation.id,
              label: limitation.title || '',
              icon: getIssueIcon(IssueType.limitation, true),
              path: idToHtmlId('#limitation', limitation.id),
            }));

            const children = [...childrenDeficiencies, ...childrenLimitations];

            let subsectionIssueCount = children.length;
            if (isSOW) {
              const hasIncomplete = subsystem.reportInformations
                ?.filter(({ dataName }) => dataName?.includes('sow_completed'))
                .some(info => {
                  const normalizedValue =
                    typeof info.value === 'string' ? info.value.replace(/^"(.*)"$/, '$1') : info.value;
                  return normalizedValue !== 'Complete';
                });

              if (hasIncomplete) {
                subsectionIssueCount += 1;
              }
            }

            return {
              id: subsystem.id,
              label: subsystem.name || '',
              path: idToHtmlId('#subsystem', subsystem.id),
              extra: subsectionIssueCount || null,
              children: children.length ? children : undefined,
            };
          })
          : undefined,
      };
    });

    return sections;
  });

export const selectFilteredReportSystemGroups = createSelector(
  [selectReport, selectReportOptions],
  (selectedReport, { filters }) => {
    const { groups, items } = filters;

    const filter = selectedReport.groupedReportSubsystemsBySystemName
      .map(system => {
        const filteredSubsystems = filterSubsystems(system.reportSubsystems, groups, items);
        return filteredSubsystems.length > 0 ? { ...system, reportSubsystems: filteredSubsystems } : null;
      })
      .filter(system => system !== null);
    return filter;
  }
);

export const selectContractorId = (state: RootState) => {
  const inspectionReports = state.selectedReport?.order?.inspections?.find(inspection =>
    inspection.inspectionReports?.find(ir => ir?.report?.id === state.selectedReport?.report?.id)
  )
  return inspectionReports?.scheduledProposal?.contractorId || '';
};

export const selectReportLimitationSummary = createSelector([
  selectReport,
], (selectedReport): LimitationSummaryRowType[] => {
  const systems = selectedReport.groupedReportSubsystemsBySystemName;
  const isMultiUnit = selectedReport.report.reportSystemGroups.length > 1;
  const filter: ReportDeliveryFilter = selectedReport.options.filters.items;

  return systems.reduce((
    issues: LimitationSummaryRowType[],
    system: ReportSystemType,
  ) => {
    const subsystems: ReportSubSystemTypeWithGroup[] = [...system.reportSubsystems].sort(sortByDisplayIndex);

    const systemIssues = subsystems.reduce((list, subsystem) => {
      const groupedDefects = subsystem.reportDeficiencies?.reduce((grouped, deficiency) => {
        if (filter === ReportDeliveryFilter.MediaOnly) {
          return grouped;
        }

        const temp: GroupedDeficienciesBySeverity = { ...grouped };
        const type = getIssueTypeForDefect(deficiency);

        if (filter === ReportDeliveryFilter.FixedDefectsOnly && type !== IssueType.fixed) {
          return temp;
        }
        if (filter === ReportDeliveryFilter.RemainingDefectsOnly && type === IssueType.fixed) {
          return temp;
        }
        if (type === IssueType.still_exists || type === IssueType.limitation) {
          return temp;
        }

        temp[type] = [
          ...temp[type],
          {
            ...deficiency,
            systemId: system.id,
            severity: deficiency.severity as ReportSeverity,
            subsystemItem: subsystem.id,
            type,
            title: deficiency.title,
            reInspectionStatus: deficiency.reInspectionStatus as ReInspectionStatus,
            systemGroupName: subsystem?.group?.name || '',
          },
        ] as LimitationSummaryRowType[];
        return temp;
      },
        {
          [IssueType.fixed]: [],
          [IssueType.safety]: [],
          [IssueType.repair]: [],
          [IssueType.monitor]: [],
          [IssueType.flag]: [],
        } as GroupedDeficienciesBySeverity,
      );

      const limitations = subsystem.reportLimitations?.reduce((ls: LimitationSummaryRowType[], limitation) => {
        const isSubsystemCannotInspect = subsystem.status === ReportStatusEnum.CannotInspect
        const isConditionalLimitation = limitation.reportInformationId !== null

        if (
          filter === ReportDeliveryFilter.MediaOnly ||
          filter === ReportDeliveryFilter.FixedDefectsOnly ||
          isSubsystemCannotInspect && isConditionalLimitation
        ) {
          return ls;
        }

        const { title } = limitation;
        const systemGroupName = isMultiUnit ? subsystem.group?.name : '';
        // const systemGroupName = isMultiUnit ? selectedReport.report?.reportSystemGroups?.find(rs => rs.id === system.reportSystemGroupId)?.name : '';
        return [...ls, {
          ...limitation,
          systemId: system.id,
          subsystemId: subsystem.id,
          type: IssueType.limitation,
          title,
          systemGroupName,
        }] as LimitationSummaryRowType[];
      }, []);

      const subsystemIssues = [
        ...(groupedDefects?.[IssueType.fixed] || []),
        ...(groupedDefects?.[IssueType.safety] || []),
        ...(groupedDefects?.[IssueType.repair] || []),
        ...(groupedDefects?.[IssueType.monitor] || []),
        ...limitations,
        ...(groupedDefects?.[IssueType.flag] || []),
      ];

      if (subsystemIssues.length) subsystemIssues[0].subsystemName = subsystem.name;

      return [...list, ...subsystemIssues];
    }, [] as LimitationSummaryRowType[]);

    if (systemIssues.length) systemIssues[0].systemName = system.name;
    return [...issues, ...systemIssues];
  }, []);
});

/**
 * Selects SOW inspection summary rows with filtering applied
 */
export const selectSowSummaryRows = createSelector(
  [selectReport],
  (selectedReport): SowSummaryRowType[] => {
    return selectedReport.groupedReportSubsystemsBySystemName
      .flatMap(({ name, reportSubsystems }) =>
        reportSubsystems.flatMap((subsystem: ReportSubSystemTypeWithGroup) => subsystem.reportInformations
          .filter(({ dataName }) => dataName?.includes('sow_completed'))
          .flatMap(info => {
            const status = info?.value && JSON.parse(info.value) === 'Complete' ? 'Complete' : 'Incomplete';
            const isComplete = status === 'Complete';
            const sanitizedDescription = info.detail?.replace(/Is the agreed upon work described complete\?/g, '').trim()

            return {
              id: subsystem.id,
              systemName: name || '',
              subsystemName: subsystem.name || '',
              description: sanitizedDescription || '',
              unit: subsystem.group?.name || null,
              status,
              isComplete,
            };
          }))
      )
  }
);

const getIssueTypeForDefect = (deficiency: ReportDeficiencyType): IssueType => (
  deficiency.reInspectionStatus === ReInspectionStatus.Fixed
    ? IssueType.fixed
    : (deficiency.severity || IssueType.limitation) as IssueType
);

export function withLabel(attachment: ReportAttachmentType, label: string, displayIndex?: Maybe<number>): ReportAttachmentListType {
  return ({ ...attachment, label, displayIndex });
}

export const selectReportAttachments = createSelector(
  [selectCurrentReport],
  (selectedReport): ReportAttachmentListType[] => {
    const systemsIncludedOnReport = selectedReport.reportSystems
      .filter(includeSystemSubSystems)
      .sort(sortByDisplayIndexAndHomeDetails);
    const reportAttachments: ReportAttachmentListType[] = [];
    const ids = new Set<string>();

    const addAttachments = (attachments: ReportAttachmentType[], label: string, displayIndex?: number) => {
      for (const attachment of attachments) {
        if (!ids.has(attachment.id)) {
          reportAttachments.push(withLabel(attachment, label, displayIndex));
          ids.add(attachment.id);
        }
      }
    };

    systemsIncludedOnReport.forEach(system => {
      addAttachments(system.reportAttachments, system.name);
      system.reportSubsystems.forEach(subsystem => {
        addAttachments(subsystem.reportAttachments, subsystem.name);
        subsystem.reportDeficiencies?.forEach(def => addAttachments(def.reportAttachments, def.title));
        subsystem.reportLimitations?.forEach(limit => addAttachments(limit.reportAttachments, limit.title));
        [...subsystem.reportInformations]
          ?.sort(sortByDisplayIndex)
          ?.forEach(info => {
            addAttachments(info.reportAttachments, info.title, info.displayIndex);
            info.reportDeficiencies?.forEach(def => addAttachments(def.reportAttachments, def.title));
            info.reportLimitations?.forEach(limit => addAttachments(limit.reportAttachments, limit.title));
          });
      });
    });

    ids.clear();
    return reportAttachments.sort(sortByDisplayIndex);
  },
);

// Photo sphere ReportAttachment ids mapped to associated FloorPlanAnchors
export const selectPhotoSphereAnchorsMap = createSelector(
  [(state: RootState) => state.selectedReport.floorPlanLevels],
  (floorPlanLevels) => {
    const photoSphereAnchorsMap: Record<string, FloorPlanAnchorType> = {};

    for (const floorPlanLevel of Object.values(floorPlanLevels)) {
      floorPlanLevel.floorPlanAnchors?.forEach(anchor => {
        if (anchor.reportAttachmentId) {
          photoSphereAnchorsMap[anchor.reportAttachmentId] = anchor;
        }
      });
    }

    return photoSphereAnchorsMap;
  }
)

export const selectPhotoSphereAttachments = createSelector(
  [selectCurrentReport, selectPhotoSphereAnchorsMap],
  (selectedReport, photoSphereAnchorsMap): ReportAttachmentListType[] => {
    const systemsIncludedOnReport = selectedReport.reportSystems
      .filter(includeSystemSubSystems)
      .sort(sortByDisplayIndexAndHomeDetails);
    const reportAttachments: ReportAttachmentListType[] = [];
    const ids = new Set<string>();

    const addAttachments = (attachments: ReportAttachmentType[], label: string, displayIndex?: Maybe<number>) => {
      for (const attachment of attachments) {
        if (!ids.has(attachment.id) && attachment.mediaType === 'photo_sphere' && attachment.attachmentUrl) {
          const item = withLabel(attachment, label, displayIndex);
          item.hasAnchor = !!photoSphereAnchorsMap?.[attachment.id];
          reportAttachments.push(item);
          ids.add(attachment.id);
        }
      }
    };

    systemsIncludedOnReport.forEach(system => {
      addAttachments(system.reportAttachments, system.name, system.displayIndex);
      system.reportSubsystems.forEach(subsystem => {
        addAttachments(subsystem.reportAttachments, subsystem.name, subsystem.displayIndex);
        subsystem.reportInformations?.forEach(info => {
          addAttachments(info.reportAttachments, subsystem.name, info.displayIndex);
        });
      });
    });

    ids.clear();
    return reportAttachments.sort(sortByDisplayIndex);
  },
);

export const selectReportSubSystemById = createSelector(
  [selectCurrentReport, (_: RootState, id: string) => id],
  (report, id) => {
    return report.reportSystems.flatMap(s => s.reportSubsystems).find(s => s.id === id);
  }
);

export const selectSubSystemAttachments = createSelector([
  selectReportSubSystemById,
  (_, __, filter?: ReportMedia[]) => filter,
], (subsystem, filter) => {
  if (!subsystem || subsystem?.status === ReportStatusEnum.NotPresent) return [];
  const attachments = [
    ...subsystem.reportAttachments.map(a => withLabel(a, subsystem.name, subsystem.displayIndex)),
    ...subsystem.reportInformations.flatMap(info => info.reportAttachments.map(a => withLabel(a, info.title, info.displayIndex))),
  ];
  // apply filter
  return attachments.filter(a => !filter || filter.includes(a.mediaType! as ReportMedia))
    .sort(sortByDisplayIndex)
});

export const selectSplitInformation = createSelector([
  (_, subsystem: ReportSubsystemType) => subsystem,
], (subsystem) => {
  if (subsystem.status === ReportStatusEnum.NotPresent) {
    return {
      appliances: [],
      informations: [],
    }
  }
  const appliances =
    subsystem.status !== ReportStatusEnum.NotPresent && subsystem.status !== ReportStatusEnum.NotStarted
      ? subsystem?.reportInformations?.filter(filterApplianceInformation)?.sort(sortByDisplayIndex) || []
      : [];

  const informations = subsystem?.reportInformations
    ?.filter(filterInformation)
    ?.sort(sortByDisplayIndex) || [];

  return { appliances, informations };
});

export const selectAddressPrintLabel = createSelector(
  [selectOrder],
  ({ property }) => {
    if (!property) return '--';
    if (property.address) return property.address;

    const { state, city, zipCode, street2, street } = property;
    return `${street}${street2 ? `, ${street2}` : ''}\n${city || ''}, ${state || ''} ${zipCode || ''}`
  },
);

export const selectSystemAttachments = createSelector(
  [selectCurrentReport, (_: RootState, id: string) => id],
  (selectedReport, id) => {
    const system = selectedReport.reportSystems.find(s => s.id === id);
    return system?.reportAttachments.map(a => withLabel(a, system.name)) || [];
  },
);

export const selectLimitationAttachments = createSelector(
  [selectCurrentReport, (_: RootState, id: string) => id],
  (selectedReport, id) => {
    const limitation = selectedReport.reportSystems
      ?.flatMap(s => s.reportSubsystems)
      .flatMap(ss => ss.reportLimitations)
      .find(l => l.id === id);

    return limitation?.reportAttachments.map(a => withLabel(a, limitation.title)) || [];
  },
);

export const selectOriginalDeficiencyAttachments = createSelector(
  [selectCurrentReport, (_: RootState, id: string) => id],
  (selectedReport, id) => {
    const deficiency = selectedReport.reportSystems
      ?.flatMap(s => s.reportSubsystems)
      .flatMap(ss => ss.reportDeficiencies)
      .find(l => l.id === id);

    return deficiency?.originalReportDeficiency?.reportAttachments.map(a => withLabel(a, deficiency?.originalReportDeficiency?.title || '')) || [];
  },
);

export const selectDeficiencyAttachments = createSelector(
  [selectCurrentReport, (_: RootState, id: string) => id],
  (selectedReport, id) => {
    const deficiency = selectedReport.reportSystems
      ?.flatMap(s => s.reportSubsystems)
      .flatMap(ss => ss.reportDeficiencies)
      .find(l => l.id === id);

    return deficiency?.reportAttachments.map(a => withLabel(a, deficiency.title)) || [];
  },
);

export const selectApplianceWarrantyInformation = createSelector(
  [selectCurrentReport],
  (report) => {
    const reportApplianceInformations = report?.reportSystems
      ?.flatMap((system) =>
        system?.reportSubsystems
          ?.flatMap(subsystem =>
            subsystem?.reportInformations.filter(info => info?.questionType === 'appliance_tag')
          ).flatMap(info => info?.reportApplianceInformation as ReportApplianceInformationType
          )
      ) || [];

    const warrantyCoveredAppliances = reportApplianceInformations
      ?.filter((appliance) => appliance?.warrantyCovered)
      || [] as ReportApplianceInformationType[];

    return warrantyCoveredAppliances;
  },
);

export const selectAppliance = createSelector([
  selectCurrentReport,
  (_: RootState, id?: Maybe<string>) => id,
], (report, informationId) => {
  const information = report?.reportSystems
    ?.flatMap(rs => rs.reportSubsystems)
    ?.flatMap(ss => ss.reportInformations)
    ?.find(ss => ss.id === informationId)
    ?.reportApplianceInformation
  if (!information) return null;
  return {
    age: information?.age,
    brandName: information?.brandName,
    expectedLife: information?.expectedLife,
    modelNumber: information?.modelNumber,
    productName: information?.productName,
    serialNumber: information?.serialNumber,
    monthManufactured: information?.monthManufactured,
    yearManufactured: information?.yearManufactured,
  };
});

export const selectPhotoSphereAnchoringComplete = createSelector(
  [
    selectPhotoSphereAttachments,
    selectFloorPlanInspections
  ],
  (photoSphereAttachments, floorPlanInspections) => {
    // Return complete by default unless it's Photosphere + Floor Plan
    if (photoSphereAttachments?.length === 0 || floorPlanInspections?.length === 0) return true;

    return photoSphereAttachments.every(({ hasAnchor }) => hasAnchor);
  }
);

export const selectManualPinningEnabled = createSelector(
  [selectReport],
  ({ report }) => report.enableManualPinning
);

export const selectPhotoSphereFloorPlanIntegrationAvailable = createSelector(
  [selectOrder, selectPhotoSphereAttachments, selectFloorPlanLevels], (order, photoSphereAttachments, floorPlanLevels) => {
    const inspectionsIncludeFloorPlan = order.inspections.some(({ inspectionType }) => inspectionType === 'floor_plan')

    const hasLevels = floorPlanLevels && Object.keys(floorPlanLevels).length > 0;

    return inspectionsIncludeFloorPlan && photoSphereAttachments.length > 0 && hasLevels;
  }
);

export const selectReportIssueCount = createSelector(
  [selectReport, selectReportOptions],
  (report, { filters }) => {
    const { items } = filters;
    if (items === ReportFilterEnum.MediaOnly) return 0;

    const systems = report.groupedReportSubsystemsBySystemName;
    return systems.reduce((count, system) => {
      const subsystems = system.reportSubsystems.filter(s => !hideSubsystem(s));
      return count + subsystems.reduce((subCount, subsystem) => {
        const deficienciesCount = subsystem.reportDeficiencies?.length || 0;
        const limitationsCount = subsystem.reportLimitations?.length || 0;
        return subCount + deficienciesCount + limitationsCount;
      }, 0);
    }, 0);
  });

export const selectReportIsPaywalled = createSelector(
  [selectOrder, selectCurrentReport, selectContractorId],
  (order, report, selectedContractorId) => {
    const currentUserId = localStorage.getItem(LocalStorageKey.SelectedContractor) || '';

    return order?.isPaywall && selectedContractorId !== currentUserId;
  }
);

export const selectFilteredOrderInspections = createSelector(
  [selectOrder, (_, filters: string[]) => filters],
  (order, filters) => order?.inspections?.filter(({ scheduledProposal, status, isPaid }) => (
    scheduledProposal && filters.includes(status || '') && !isPaid
  )));


export const selectMainStakeholderName = createSelector(
  [selectOrder],
  (order) => order?.orderStakeholders?.filter(hideStakeholder)?.[0]?.name || '--'
);

export const selectOrderScheduledAt = createSelector(
  [
    selectOrder,
    (_: RootState, orderId: Maybe<string> | undefined) => orderId,
    (_: RootState, __: Maybe<string> | undefined, dateFormat = FORMAT_PPP) => dateFormat
  ],
  (order, orderId, dateFormat) => {
    const inspectionDate = order?.inspections?.find(inspection =>
      inspection?.orderId === orderId && !!inspection?.scheduledProposal
    )?.scheduledProposal?.scheduledAt;
    return format(fromUnixTime(inspectionDate || 0), dateFormat);
  }
);

export const selectReportPublishedAt = createSelector(
  [selectCurrentReport, selectOrder, (_: RootState, orderId: any, dateFormat: string) => dateFormat],
  (report, order, dateFormat = FORMAT_PPP) => {
    return report?.publishedAt
      ? format(fromUnixTime(report.publishedAt), dateFormat)
      : '--'; //{ timeZone: order?.property?.timezone || 'UTC' }
  }
);

export const selectReportTypeString = createSelector(
  [selectOrder],
  (order) => {
    const inspections = order?.inspections || [];
    const types = inspections?.reduce((acc: Array<string>, inspection) => {
      if (acc.indexOf(inspection?.inspectionType) === -1) {
        acc.push(inspection?.inspectionType);
      }
      return acc;
    }, []);
    const sentence = toCapitalizedSentence(types);
    return `${sentence} Report - ${order?.property?.street || ''}`;
  }
);

export const selectCurrentInspection = createSelector(
  [selectOrder, selectCurrentReportId],
  (order, reportId) => order?.inspections?.find(inspection =>
    inspection?.inspectionReports?.some(report => report?.report?.id === reportId)
  ) || null
);


const distanceToPropertyMeters = (
  attachment: ReportAttachmentListType,
  property: PropertyType,
) => {
  const attachmentCoordinates = JSON.parse(attachment?.metadata || 'null')?.gps;

  if (!property?.latitude || !property?.longitude || !attachmentCoordinates) {
    return null;
  }

  return haversineDistance(attachmentCoordinates, {
    latitude: property.latitude,
    longitude: property.longitude,
  });
};

export const ATTACHMENT_OUTSIDE_PROPERTY_THRESHOLD_FEET = 200;

const attachmentIsTooFar = (attachment: ReportAttachmentListType, property: PropertyType) => {
  const distanceMeters = distanceToPropertyMeters(attachment, property);

  if (!distanceMeters) return false;

  return metersToFeet(distanceMeters) > ATTACHMENT_OUTSIDE_PROPERTY_THRESHOLD_FEET;
};

export const selectDistanceToPropertyFeet = createSelector(
  [
    selectProperty,
    selectReport,
    (_, attachment) => attachment
  ],
  (selectedProperty, selectedReport, attachment) => {
    if (!selectedReport.report.geotaggingRequired || !selectedProperty) return null;

    const distanceMeters = distanceToPropertyMeters(attachment, selectedProperty);

    if (distanceMeters === null) return null;

    return metersToFeet(distanceMeters);
  }
);

export const selectPhotoCountOutsideProperty = createSelector(
  [
    selectReportAttachments,
    selectProperty,
  ],
  (attachments, property): number => {
    return attachments.reduce((count, attachment) => {
      const attachmentCoordinates = JSON.parse(attachment?.metadata || 'null')?.gps;
      if (!attachmentCoordinates) return count;
      if (!property?.latitude || !property?.longitude) {
        return count + 1;
      }
      if (attachmentIsTooFar(attachment, property)) {
        return count + 1;
      }
      return count;
    }, 0);
  }
);
